import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { rptV3Api } from '../services/api';

function RPTV3GLNatureMappingEditor({
  entity,
  natures,
  onMappingsChanged,
  compact = false,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeBuiltin, setIncludeBuiltin] = useState(false);
  const [form, setForm] = useState({
    gl_code: '',
    nature: '',
    description: '',
    enabled: true,
  });

  const loadMappings = useCallback(async () => {
    if (!entity?.name) return;
    setLoading(true);
    setError(null);
    try {
      const response = await rptV3Api.listGLNatureMappings(entity.name, includeBuiltin);
      setConfig(response);
      if (onMappingsChanged) {
        onMappingsChanged(response);
      }
    } catch (err) {
      console.error('Failed to load RPT V3 GL-nature mappings:', err);
      setError(err.message || err.data?.error || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [entity?.name, includeBuiltin, onMappingsChanged]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  useEffect(() => {
    if (!form.nature && natures.length > 0) {
      setForm((prev) => ({ ...prev, nature: natures[0] }));
    }
  }, [natures, form.nature]);

  useEffect(() => {
    if (!includeBuiltin && ['baseline', 'overridden'].includes(statusFilter)) {
      setStatusFilter('all');
    }
  }, [includeBuiltin, statusFilter]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      gl_code: '',
      nature: natures[0] || '',
      description: '',
      enabled: true,
    });
  };

  const handleEdit = (mapping) => {
    setEditingId(mapping.id);
    setForm({
      gl_code: mapping.gl_code || '',
      nature: mapping.nature || natures[0] || '',
      description: mapping.description || '',
      enabled: mapping.enabled !== false,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.gl_code.trim() || !form.nature) {
      setError('GL code and nature are required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await rptV3Api.updateGLNatureMapping(editingId, form);
      } else {
        await rptV3Api.addGLNatureMapping(entity.name, form);
      }
      resetForm();
      await loadMappings();
    } catch (err) {
      console.error('Failed to save RPT V3 GL-nature mapping:', err);
      setError(err.message || err.data?.error || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (mappingId) => {
    setSaving(true);
    setError(null);
    try {
      await rptV3Api.disableGLNatureMapping(mappingId);
      await loadMappings();
      if (editingId === mappingId) {
        resetForm();
      }
    } catch (err) {
      console.error('Failed to disable RPT V3 GL-nature mapping:', err);
      setError(err.message || err.data?.error || 'Failed to disable mapping');
    } finally {
      setSaving(false);
    }
  };

  const userMappings = useMemo(() => config?.mappings || [], [config]);
  const baselineMappings = useMemo(() => config?.baseline_mappings || [], [config]);
  const mappings = useMemo(() => {
    const normalizedUserMappings = userMappings.map((mapping) => ({
      ...mapping,
      source: mapping.source || 'user',
      read_only: false,
    }));
    return [...normalizedUserMappings, ...baselineMappings];
  }, [userMappings, baselineMappings]);
  const activeCount = userMappings.filter((mapping) => mapping.enabled).length;
  const disabledCount = userMappings.length - activeCount;
  const baselineCount = baselineMappings.length;
  const sortedMappings = useMemo(() => {
    return [...mappings].sort((a, b) => {
      const aUpdated = Date.parse(a.updated_at || '') || 0;
      const bUpdated = Date.parse(b.updated_at || '') || 0;
      if (aUpdated !== bUpdated) {
        return bUpdated - aUpdated;
      }
      return String(a.gl_code || '').localeCompare(String(b.gl_code || ''));
    });
  }, [mappings]);
  const latestMapping = sortedMappings[0];
  const filteredMappings = sortedMappings.filter((mapping) => {
    if (statusFilter === 'active' && (mapping.source === 'baseline' || !mapping.enabled)) return false;
    if (statusFilter === 'disabled' && (mapping.source === 'baseline' || mapping.enabled)) return false;
    if (statusFilter === 'baseline' && !['baseline', 'engine'].includes(mapping.source)) return false;
    if (statusFilter === 'overridden' && !mapping.overridden) return false;
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return true;
    return [
      mapping.gl_code,
      mapping.nature,
      mapping.description,
      mapping.updated_by,
      mapping.source,
      mapping.id,
    ].some((value) => String(value || '').toLowerCase().includes(needle));
  });

  const formatUpdatedAt = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  return (
    <div className={`rpt-v3-mapping-editor ${compact ? 'compact' : ''}`}>
      <div className="mapping-editor-header">
        <div>
          <h4>GL Nature Mapping Overrides</h4>
          <p>
            Review built-in codebase rules and maintain optional YAML overrides for this
            reporting entity.
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={loadMappings} disabled={loading || saving}>
          Refresh
        </button>
      </div>

      {config && (
        <div className="mapping-summary-grid">
          <div className="mapping-summary-card">
            <span className="summary-card-label">Reporting Entity</span>
            <strong>{config.reporting_entity}</strong>
          </div>
          <div className="mapping-summary-card">
            <span className="summary-card-label">User Active</span>
            <strong>{activeCount}</strong>
          </div>
          <div className="mapping-summary-card">
            <span className="summary-card-label">User Disabled</span>
            <strong>{disabledCount}</strong>
          </div>
          <div className="mapping-summary-card">
            <span className="summary-card-label">Built-in</span>
            <strong>{includeBuiltin ? baselineCount : 'Hidden'}</strong>
          </div>
          <div className="mapping-summary-card">
            <span className="summary-card-label">Latest Update</span>
            <strong>{latestMapping ? formatUpdatedAt(latestMapping.updated_at) : 'No updates'}</strong>
            {latestMapping?.updated_by && <small>by {latestMapping.updated_by}</small>}
          </div>
          <div className="mapping-summary-card hash-card">
            <span className="summary-card-label">Config Hash</span>
            <strong>{config.config_hash || 'N/A'}</strong>
          </div>
        </div>
      )}

      <div className="builtin-toggle">
        <label>
          <input
            type="checkbox"
            checked={includeBuiltin}
            onChange={(event) => setIncludeBuiltin(event.target.checked)}
            disabled={loading || saving}
          />
          Show built-in baseline and engine rules
        </label>
        <span>
          Built-in rows are read-only and fetched only when this option is enabled.
        </span>
      </div>

      {error && (
        <div className="alert alert-danger mapping-alert">
          <strong>Mapping error:</strong> {error}
        </div>
      )}

      <form className="mapping-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="GL code"
          value={form.gl_code}
          onChange={(event) => setForm({ ...form, gl_code: event.target.value })}
          disabled={saving}
        />
        <select
          value={form.nature}
          onChange={(event) => setForm({ ...form, nature: event.target.value })}
          disabled={saving || natures.length === 0}
        >
          {natures.map((nature) => (
            <option key={nature} value={nature}>
              {nature}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Description (optional)"
          value={form.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
          disabled={saving}
        />
        <label className="mapping-enabled">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
            disabled={saving}
          />
          Enabled
        </label>
        <button className="btn btn-v3 btn-sm" type="submit" disabled={saving || natures.length === 0}>
          {editingId ? 'Save Mapping' : 'Add Mapping'}
        </button>
        {editingId && (
          <button className="btn btn-secondary btn-sm" type="button" onClick={resetForm} disabled={saving}>
            Cancel Edit
          </button>
        )}
      </form>

      {mappings.length > 0 && (
        <div className="mapping-toolbar">
          <input
            type="text"
            placeholder="Search GL code, nature, description, updated by..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All mappings</option>
            <option value="active">User active only</option>
            <option value="disabled">User disabled only</option>
            {includeBuiltin && <option value="baseline">Built-in only</option>}
            {includeBuiltin && <option value="overridden">Overridden baseline only</option>}
          </select>
          <span>
            Showing {filteredMappings.length} of {mappings.length}
          </span>
        </div>
      )}

      <div className="mapping-table-wrap">
        {loading ? (
          <div className="mapping-empty">Loading mappings...</div>
        ) : mappings.length === 0 ? (
          <div className="mapping-empty">No user-maintained mappings configured.</div>
        ) : filteredMappings.length === 0 ? (
          <div className="mapping-empty">No mappings match the current filter.</div>
        ) : (
          <table className="mapping-table">
            <thead>
              <tr>
                <th>GL Code</th>
                <th>Nature</th>
                <th>Source</th>
                <th>Status</th>
                <th>Description</th>
                <th>Updated</th>
                <th>Updated By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMappings.map((mapping) => (
                <tr
                  key={mapping.id}
                  className={`${mapping.enabled ? '' : 'disabled-row'} ${['baseline', 'engine'].includes(mapping.source) ? 'baseline-row' : ''}`}
                >
                  <td>{mapping.gl_code}</td>
                  <td>{mapping.nature}</td>
                  <td>
                    {mapping.source === 'baseline'
                      ? 'Baseline mapping'
                      : mapping.source === 'engine'
                        ? 'Engine rule'
                        : 'User override'}
                  </td>
                  <td>
                    {['baseline', 'engine'].includes(mapping.source)
                      ? (mapping.overridden ? 'Overridden' : 'Read-only')
                      : (mapping.enabled ? 'Enabled' : 'Disabled')}
                  </td>
                  <td>{mapping.description || '-'}</td>
                  <td>{formatUpdatedAt(mapping.updated_at)}</td>
                  <td>{mapping.updated_by || '-'}</td>
                  <td>
                    {mapping.read_only ? (
                      <span className="read-only-label">Code-defined</span>
                    ) : (
                      <>
                        <button
                          className="link-btn"
                          type="button"
                          onClick={() => handleEdit(mapping)}
                          disabled={saving}
                        >
                          Edit
                        </button>
                        {mapping.enabled && (
                          <button
                            className="link-btn danger"
                            type="button"
                            onClick={() => handleDisable(mapping.id)}
                            disabled={saving}
                          >
                            Disable
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <style jsx>{`
        .rpt-v3-mapping-editor {
          margin-top: 20px;
          padding: 14px;
          border: 1px solid #bbf7d0;
          background: #f0fdf4;
          border-radius: 8px;
        }
        .rpt-v3-mapping-editor.compact {
          margin-top: 12px;
        }
        .mapping-editor-header {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: flex-start;
        }
        .mapping-editor-header h4 {
          margin: 0 0 6px 0;
          color: #166534;
          font-size: 14px;
        }
        .mapping-editor-header p {
          margin: 0;
          color: #166534;
          font-size: 12px;
        }
        .mapping-summary-grid {
          display: grid;
          grid-template-columns: 1.5fr 0.7fr 0.7fr 0.7fr 1.5fr 1.1fr;
          gap: 10px;
          margin-top: 10px;
        }
        .mapping-summary-card {
          background: #dcfce7;
          border: 1px solid #86efac;
          border-radius: 8px;
          padding: 9px 10px;
          color: #166534;
          min-width: 0;
        }
        .mapping-summary-card strong {
          display: block;
          font-size: 13px;
          overflow-wrap: anywhere;
        }
        .mapping-summary-card small {
          display: block;
          margin-top: 3px;
          color: #15803d;
          font-size: 11px;
        }
        .summary-card-label {
          display: block;
          margin-bottom: 3px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #15803d;
        }
        .hash-card strong {
          font-family: monospace;
        }
        .mapping-alert {
          margin-top: 10px;
        }
        .builtin-toggle {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: center;
          margin-top: 12px;
          padding: 8px 10px;
          border: 1px solid #bbf7d0;
          border-radius: 6px;
          background: #f8fff9;
          color: #166534;
          font-size: 12px;
        }
        .builtin-toggle label {
          display: inline-flex;
          gap: 6px;
          align-items: center;
          font-weight: 600;
        }
        .builtin-toggle span {
          color: #15803d;
          font-size: 11px;
        }
        .mapping-form {
          display: grid;
          grid-template-columns: 100px 1fr 1fr auto auto auto;
          gap: 8px;
          align-items: center;
          margin-top: 12px;
        }
        .mapping-form input[type='text'],
        .mapping-form select {
          min-width: 0;
          padding: 7px 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 12px;
        }
        .mapping-enabled {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          color: #166534;
          white-space: nowrap;
        }
        .btn-sm {
          padding: 7px 10px;
          font-size: 12px;
        }
        .mapping-table-wrap {
          margin-top: 12px;
          max-height: 220px;
          overflow: auto;
          background: white;
          border: 1px solid #dcfce7;
          border-radius: 6px;
        }
        .mapping-empty {
          padding: 12px;
          color: #64748b;
          font-size: 12px;
        }
        .mapping-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .mapping-table th,
        .mapping-table td {
          padding: 8px;
          border-bottom: 1px solid #e5e7eb;
          text-align: left;
          vertical-align: top;
        }
        .mapping-table th {
          background: #dcfce7;
          color: #166534;
          font-weight: 600;
          position: sticky;
          top: 0;
        }
        .disabled-row {
          color: #94a3b8;
          background: #f8fafc;
        }
        .baseline-row {
          background: #f8fafc;
        }
        .baseline-row td {
          color: #475569;
        }
        .link-btn {
          border: none;
          background: none;
          color: #0369a1;
          cursor: pointer;
          padding: 0 8px 0 0;
          font-size: 12px;
        }
        .link-btn.danger {
          color: #b91c1c;
        }
        .link-btn:disabled {
          color: #94a3b8;
          cursor: not-allowed;
        }
        .read-only-label {
          color: #64748b;
          font-size: 12px;
          white-space: nowrap;
        }
        .mapping-toolbar {
          display: grid;
          grid-template-columns: minmax(220px, 1fr) 150px auto;
          gap: 8px;
          align-items: center;
          margin-top: 12px;
          color: #166534;
          font-size: 12px;
        }
        .mapping-toolbar input,
        .mapping-toolbar select {
          min-width: 0;
          padding: 7px 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 12px;
          background: white;
        }
        @media (max-width: 900px) {
          .mapping-summary-grid,
          .mapping-toolbar,
          .builtin-toggle,
          .mapping-form {
            grid-template-columns: 1fr;
            display: grid;
          }
        }
      `}</style>
    </div>
  );
}

export default RPTV3GLNatureMappingEditor;
